<template>
  <div class="member-search-component">
    <div class="form-group">
      <label class="form-label">搜尋會員</label>
      <div class="search-input-wrap">
        <input
          ref="searchInputRef"
          v-model="searchQuery"
          type="text"
          class="form-control"
          :placeholder="searchPlaceholder"
          @input="handleSearch"
          :disabled="disabled"
        />
        <i class="ri-search-line"></i>
      </div>
      <small class="form-text">輸入至少 2 個字元開始搜尋，點擊會員加入列表</small>
    </div>

    <div v-if="isSingleSelect && selectedMembers.length" class="selected-member-banner">
      <div class="selected-member-card">
        <div class="selected-member-card-main">
          <span class="selected-badge">
            <i class="ri-check-double-line"></i>
            已選擇會員
          </span>
          <div class="selected-member-info">
            <strong>{{ selectedMembers[0].name }}</strong>
            <span v-if="selectedMembers[0].phone" class="selected-member-meta">{{ selectedMembers[0].phone }}</span>
            <span v-if="selectedMembers[0].email" class="selected-member-meta">{{ selectedMembers[0].email }}</span>
          </div>
        </div>
        <button
          type="button"
          class="btn-change-member"
          :disabled="disabled"
          @click="clearSelection"
        >
          更換
        </button>
      </div>
    </div>

    <div v-if="searchQuery && searching" class="searching-hint">
      <i class="ri-loader-4-line spin"></i> 搜尋中...
    </div>

    <div
      v-if="searchQuery && !searching && results.length > 0 && !(isSingleSelect && selectedMembers.length)"
      class="member-results"
    >
      <div class="results-header">
        <span>找到 {{ results.length }} 位會員</span>
        <button
          v-if="!isSingleSelect"
          type="button"
          class="btn btn-sm btn-link"
          @click="addAll"
          :disabled="disabled"
        >
          <i class="ri-add-line"></i>
          全部加入
        </button>
      </div>
      <div class="member-list">
        <div
          v-for="member in results"
          :key="member.id"
          class="member-item"
          :class="{ selected: isSelected(member.id) }"
          @click="toggle(member)"
        >
          <div class="member-select-indicator" aria-hidden="true">
            <i v-if="isSelected(member.id)" class="ri-checkbox-circle-fill"></i>
            <i v-else class="ri-checkbox-blank-circle-line"></i>
          </div>
          <div class="member-info">
            <div class="member-name">{{ member.name }}</div>
            <div class="member-details">
              <span v-if="member.phone">{{ member.phone }}</span>
              <span v-if="member.email">{{ member.email }}</span>
            </div>
          </div>
          <span v-if="isSelected(member.id)" class="selected-tag">已選取</span>
        </div>
      </div>
    </div>

    <div v-if="searchQuery && !searching && results.length === 0" class="no-results">
      <i class="ri-user-search-line"></i>
      <p>找不到符合的會員</p>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { memberService } from '@/api/modules/members.js';

const DEFAULT_PLACEHOLDER = '輸入會員名稱、電話或 Email 搜尋...';

export default {
  name: 'MemberSearch',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /** 最多可選幾位；設為 1 時為單選，並會 emit selected / cleared */
    maxSelections: {
      type: Number,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'selected', 'cleared'],
  setup(props, { emit, expose }) {
    const searchInputRef = ref(null);
    const searchQuery = ref('');
    const results = ref([]);
    const searching = ref(false);
    const searchTimeout = ref(null);

    const searchPlaceholder = computed(() =>
      props.placeholder && props.placeholder.trim() ? props.placeholder : DEFAULT_PLACEHOLDER
    );

    const isSingleSelect = computed(() => props.maxSelections === 1);

    const selectedMembers = computed(() =>
      Array.isArray(props.modelValue) ? props.modelValue : []
    );

    const isSelected = (memberId) => selectedMembers.value.some(m => m.id === memberId);

    const emitSingleSelectionEvents = (list) => {
      if (!isSingleSelect.value) return;
      const m = list[0];
      emit('selected', m || null);
      if (!list.length) emit('cleared');
    };

    const toggle = (member) => {
      let current = [...selectedMembers.value];
      const index = current.findIndex(m => m.id === member.id);
      if (index > -1) {
        current.splice(index, 1);
      } else if (isSingleSelect.value) {
        current = [member];
      } else if (
        props.maxSelections != null &&
        current.length >= props.maxSelections
      ) {
        return;
      } else {
        current.push(member);
      }
      emit('update:modelValue', current);
      emitSingleSelectionEvents(current);
      if (isSingleSelect.value && current.length === 1) {
        searchQuery.value = '';
        results.value = [];
      }
    };

    const clearSelection = () => {
      emit('update:modelValue', []);
      emitSingleSelectionEvents([]);
      searchQuery.value = '';
      results.value = [];
      focus();
    };

    const addAll = () => {
      if (isSingleSelect.value) {
        const first = results.value[0];
        if (!first) return;
        const next = [first];
        emit('update:modelValue', next);
        emitSingleSelectionEvents(next);
        return;
      }
      const current = [...selectedMembers.value];
      results.value.forEach(member => {
        if (!current.some(m => m.id === member.id)) current.push(member);
      });
      emit('update:modelValue', current);
    };

    const handleSearch = () => {
      if (searchTimeout.value) clearTimeout(searchTimeout.value);
      if (!searchQuery.value || searchQuery.value.length < 2) {
        results.value = [];
        return;
      }
      searchTimeout.value = setTimeout(async () => {
        searching.value = true;
        try {
          const response = await memberService.getMembers({ search: searchQuery.value, pageSize: 50 });
          if (response?.success && response.data) {
            results.value = response.data.items || [];
          }
        } catch (err) {
          console.error('搜尋會員失敗:', err);
          results.value = [];
        } finally {
          searching.value = false;
        }
      }, 500);
    };

    const reset = () => {
      searchQuery.value = '';
      results.value = [];
      if (searchTimeout.value) clearTimeout(searchTimeout.value);
    };

    const focus = () => {
      searchInputRef.value?.focus?.();
    };

    expose({ reset, focus });

    return {
      searchInputRef,
      searchPlaceholder,
      isSingleSelect,
      searchQuery,
      results,
      searching,
      isSelected,
      toggle,
      addAll,
      handleSearch,
      clearSelection,
      selectedMembers
    };
  }
};
</script>

<style scoped>
.search-input-wrap {
  position: relative;
}

.search-input-wrap i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.searching-hint {
  font-size: 13px;
  color: #6c757d;
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.selected-member-banner {
  margin-top: 12px;
}

.selected-member-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border: 2px solid #22c55e;
  border-radius: 10px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.12);
}

.selected-member-card-main {
  min-width: 0;
  flex: 1;
}

.selected-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #16a34a;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 8px;
}

.selected-badge i {
  font-size: 14px;
}

.selected-member-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.selected-member-info strong {
  font-size: 16px;
  color: #14532d;
}

.selected-member-meta {
  font-size: 13px;
  color: #166534;
}

.btn-change-member {
  flex-shrink: 0;
  border: 1px solid #16a34a;
  background: #fff;
  color: #15803d;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.btn-change-member:hover:not(:disabled) {
  background: #f0fdf4;
  border-color: #15803d;
}

.btn-change-member:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.member-results {
  margin-top: 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  font-size: 14px;
  color: #666;
  border-bottom: 1px solid #e9ecef;
}

.member-list {
  max-height: 300px;
  overflow-y: auto;
}

.member-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s, box-shadow 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.member-item:hover { background: #f8f9fa; }

.member-item.selected {
  background: #ecfdf5;
  border-left: 4px solid #22c55e;
  box-shadow: inset 0 0 0 1px #bbf7d0;
}

.member-item:last-child { border-bottom: none; }

.member-select-indicator {
  flex-shrink: 0;
  font-size: 22px;
  line-height: 1;
  color: #cbd5e1;
}

.member-item.selected .member-select-indicator {
  color: #16a34a;
}

.member-info {
  flex: 1;
  min-width: 0;
}

.member-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.member-item.selected .member-name {
  color: #14532d;
}

.selected-tag {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: #16a34a;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.member-details {
  font-size: 13px;
  color: #666;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.no-results {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-results i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
</style>
